import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import api from '@/services/api'
import { setMetaTags, injectSchema, SEOMetaData } from '@/utils/seo'
// Build-time snapshot of page_seo (from the DB) so prerendered/no-JS pages get the
// real admin-managed meta. Runtime still refetches from the API for live values.
import seoMetaSnapshot from '@/data/seoMeta.json'

interface PageSEO {
  page_slug: string
  page_name?: string
  page_path?: string
  meta_title: string
  meta_description: string
  meta_keywords?: string
  og_title?: string
  og_description?: string
  og_image?: string
  twitter_title?: string
  twitter_description?: string
  twitter_image?: string
  canonical_url?: string
  robots_index: boolean
  robots_follow: boolean
  schema_type?: string
  schema_data?: string
}

async function fetchPageSEO(pageSlug: string): Promise<PageSEO> {
  const response = await api.get(`/seo/page/${pageSlug}`)
  return response.data
}

const seoSnapshot = seoMetaSnapshot as unknown as Record<string, PageSEO>

export function useSEO(pageSlug: string) {
  const { data: seoData, isLoading } = useQuery({
    queryKey: ['seo', pageSlug],
    queryFn: () => fetchPageSEO(pageSlug),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    retry: 1,
    // Build-time DB snapshot: used during prerender (no backend) and as instant
    // placeholder at runtime; the query still refetches live meta from the API.
    placeholderData: seoSnapshot[pageSlug],
  })

  useEffect(() => {
    if (!seoData) return

    // Build meta data object
    const meta: SEOMetaData = {
      title: seoData.meta_title,
      description: seoData.meta_description,
      ogUrl: window.location.href,
    }

    // Add keywords if available
    if (seoData.meta_keywords) {
      meta.keywords = seoData.meta_keywords.split(',').map(k => k.trim())
    }

    // Add Open Graph data
    if (seoData.og_title) meta.ogTitle = seoData.og_title
    if (seoData.og_description) meta.ogDescription = seoData.og_description
    if (seoData.og_image) meta.ogImage = seoData.og_image

    // Add canonical URL
    if (seoData.canonical_url) meta.canonical = seoData.canonical_url

    // Set meta tags
    setMetaTags(meta)

    // Set robots meta. Default to INDEX/FOLLOW — only an EXPLICIT false makes a page
    // noindex/nofollow. (A missing/NULL value must never silently de-index a public page;
    // that previously noindexed the whole main site when page_seo.robots_index was NULL.)
    const robotsContent: string[] = []
    robotsContent.push(seoData.robots_index === false ? 'noindex' : 'index')
    robotsContent.push(seoData.robots_follow === false ? 'nofollow' : 'follow')

    let robotsMeta = document.querySelector('meta[name="robots"]')
    if (!robotsMeta) {
      robotsMeta = document.createElement('meta')
      robotsMeta.setAttribute('name', 'robots')
      document.head.appendChild(robotsMeta)
    }
    robotsMeta.setAttribute('content', robotsContent.join(', '))

    // Add Twitter card meta tags
    if (seoData.twitter_title || seoData.twitter_description || seoData.twitter_image) {
      // Twitter card type
      let twitterCard = document.querySelector('meta[name="twitter:card"]')
      if (!twitterCard) {
        twitterCard = document.createElement('meta')
        twitterCard.setAttribute('name', 'twitter:card')
        document.head.appendChild(twitterCard)
      }
      twitterCard.setAttribute('content', seoData.twitter_image ? 'summary_large_image' : 'summary')

      // Twitter title
      if (seoData.twitter_title) {
        let twitterTitle = document.querySelector('meta[name="twitter:title"]')
        if (!twitterTitle) {
          twitterTitle = document.createElement('meta')
          twitterTitle.setAttribute('name', 'twitter:title')
          document.head.appendChild(twitterTitle)
        }
        twitterTitle.setAttribute('content', seoData.twitter_title)
      }

      // Twitter description
      if (seoData.twitter_description) {
        let twitterDesc = document.querySelector('meta[name="twitter:description"]')
        if (!twitterDesc) {
          twitterDesc = document.createElement('meta')
          twitterDesc.setAttribute('name', 'twitter:description')
          document.head.appendChild(twitterDesc)
        }
        twitterDesc.setAttribute('content', seoData.twitter_description)
      }

      // Twitter image
      if (seoData.twitter_image) {
        let twitterImage = document.querySelector('meta[name="twitter:image"]')
        if (!twitterImage) {
          twitterImage = document.createElement('meta')
          twitterImage.setAttribute('name', 'twitter:image')
          document.head.appendChild(twitterImage)
        }
        twitterImage.setAttribute('content', seoData.twitter_image)
      }
    }

    // Inject schema if available
    if (seoData.schema_type && seoData.schema_data) {
      try {
        const schemaData = JSON.parse(seoData.schema_data)
        schemaData['@context'] = 'https://schema.org'
        schemaData['@type'] = seoData.schema_type
        injectSchema(JSON.stringify(schemaData), `${pageSlug}-schema`)
      } catch (e) {
        console.error('Failed to parse schema data:', e)
      }
    }
  }, [seoData, pageSlug])

  return { seoData, isLoading }
}
